import { createFileRoute, Link } from "@tanstack/react-router";

import { Footer, Nav } from "../components/lidoo/chrome";
import {
  priceIndex,
  type PriceBucket,
  type PriceIndexData,
} from "../lib/api/lidoo.functions";

// The Sunbed Price Index: what a day at the beach actually costs, computed
// live from the catalogue's verified prices. English-only on purpose: this is
// an original data publication, not template chrome, and it only exists in
// the language it was researched in. Everything on it follows the same rule
// as the club pages: no price without a source, and no statistics that blend
// incomparable things (see the bucket logic in lidoo.functions.ts).
export const Route = createFileRoute("/sunbed-prices")({
  loader: async () => await priceIndex(),
  head: (ctx) => {
    const n = ctx.loaderData?.pricedClubs ?? 0;
    return {
      meta: [
        { title: `Sunbed Price Index 2026: what a beach club day costs | sunbedly` },
        {
          name: "description",
          content: `Verified sunbed and day pass prices from ${n} beach clubs across 8 countries. Medians per country, the cheapest and most expensive beds in Europe, and how we check every number.`,
        },
      ],
      links: [{ rel: "canonical", href: "https://sunbedly.com/sunbed-prices" }],
    };
  },
  component: () => <PriceIndexPage {...Route.useLoaderData()} />,
});

const BUCKET_LABEL: Record<PriceBucket, string> = {
  bed: "Single sunbed, per day",
  set: "Two beds + parasol, per day",
  daypass: "Day pass, per person",
};

// Stated, not hidden: the AED conversion used in the comparison column.
const AED_TO_EUR = 0.25;

function Price({ value, currency }: { value: number; currency: string }) {
  if (currency === "AED") {
    return (
      <span>
        AED {value}
        <span className="text-[#5B6472]"> (~€{Math.round(value * AED_TO_EUR)})</span>
      </span>
    );
  }
  return <span>€{value}</span>;
}

function PriceIndexPage(data: PriceIndexData) {
  const year = 2026;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: `Sunbedly Sunbed Price Index ${year}`,
    description: `Verified published prices for sunbeds, bed sets and day passes at ${data.pricedClubs} beach clubs, collected from the clubs' own published rates.`,
    url: "https://sunbedly.com/sunbed-prices",
    creator: { "@type": "Organization", name: "sunbedly", url: "https://sunbedly.com" },
    temporalCoverage: String(year),
    license: "https://sunbedly.com/terms",
  };

  return (
    <div className="min-h-dvh bg-[#F2EFE9]">
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Nav />
      <main className="mx-auto max-w-4xl px-5 pb-20 pt-12">
        <p className="li-mono text-[11px] font-bold uppercase tracking-[0.18em] text-[#2C4FD6]">
          Original data · updated live from the catalogue
        </p>
        <h1 className="li-serif mt-2 text-4xl font-normal leading-tight text-[#1C2430] md:text-5xl">
          The Sunbed Price Index {year}
        </h1>
        <p className="mt-4 max-w-[65ch] text-base leading-relaxed text-[#5B6472]">
          What does a day at a beach club actually cost? We track the published rates of{" "}
          {data.totalClubs} clubs in 19 countries. {data.pricedClubs} of them publish a
          price we could verify at the source; those are the only numbers below. The rest
          of the catalogue shows no price at all, because on this site a missing number
          beats a wrong one. <Link to="/how-we-verify" className="text-[#2C4FD6] underline underline-offset-4">How we verify every price</Link>.
        </p>

        <h2 className="li-serif mt-14 text-2xl font-normal text-[#1C2430] md:text-3xl">
          The median price per country
        </h2>
        <p className="mt-2 max-w-[65ch] text-sm leading-relaxed text-[#5B6472]">
          Medians, not averages: one five-star resort should not drag a whole coastline
          up. Prices are only compared within the same unit, and a figure is only shown
          where we hold at least four verified data points.
        </p>
        <div className="mt-6 overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-[#1C2430]/25 text-left">
                <th className="li-mono py-3 pr-4 text-[11px] font-bold uppercase tracking-widest text-[#5B6472]">Country</th>
                <th className="li-mono py-3 pr-4 text-[11px] font-bold uppercase tracking-widest text-[#5B6472]">Unit</th>
                <th className="li-mono py-3 pr-4 text-right text-[11px] font-bold uppercase tracking-widest text-[#5B6472]">Median</th>
                <th className="li-mono py-3 pr-4 text-right text-[11px] font-bold uppercase tracking-widest text-[#5B6472]">Range</th>
                <th className="li-mono py-3 text-right text-[11px] font-bold uppercase tracking-widest text-[#5B6472]">Clubs</th>
              </tr>
            </thead>
            <tbody>
              {data.countries.flatMap((c) =>
                (Object.entries(c.buckets) as [PriceBucket, { n: number; median: number; min: number; max: number }][]).map(
                  ([bucket, stats], i) => (
                    <tr key={`${c.country}-${bucket}`} className="border-b border-[#1C2430]/10">
                      <td className="py-3 pr-4 font-bold text-[#1C2430]">{i === 0 ? c.country : ""}</td>
                      <td className="py-3 pr-4 text-[#5B6472]">{BUCKET_LABEL[bucket]}</td>
                      <td className="py-3 pr-4 text-right font-bold tabular-nums text-[#1C2430]">
                        <Price value={stats.median} currency={c.currency} />
                      </td>
                      <td className="py-3 pr-4 text-right tabular-nums text-[#5B6472]">
                        {c.currency === "AED" ? "AED " : "€"}{stats.min} – {c.currency === "AED" ? "AED " : "€"}{stats.max}
                      </td>
                      <td className="py-3 text-right tabular-nums text-[#5B6472]">{stats.n}</td>
                    </tr>
                  ),
                ),
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-14 grid gap-10 md:grid-cols-2">
          <section>
            <h2 className="li-serif text-2xl font-normal text-[#1C2430]">
              The cheapest verified beds
            </h2>
            <p className="mt-1 text-sm text-[#5B6472]">Single sunbed per day, euro countries.</p>
            <ol className="mt-4 flex flex-col">
              {data.cheapest.map((c, i) => (
                <li key={c.slug} className="flex items-baseline gap-3 border-t border-[#1C2430]/10 py-3">
                  <span className="li-mono w-5 shrink-0 text-[12px] text-[#5B6472]">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <Link to="/club/$slug" params={{ slug: c.slug }} className="font-bold text-[#1C2430] hover:text-[#2C4FD6]">
                      {c.name}
                    </Link>
                    <span className="text-sm text-[#5B6472]"> · {c.town}, {c.country}</span>
                  </div>
                  <span className="shrink-0 font-bold tabular-nums text-[#1C2430]">€{c.price}</span>
                </li>
              ))}
            </ol>
          </section>
          <section>
            <h2 className="li-serif text-2xl font-normal text-[#1C2430]">
              The most expensive
            </h2>
            <p className="mt-1 text-sm text-[#5B6472]">Same unit, same rules.</p>
            <ol className="mt-4 flex flex-col">
              {data.dearest.map((c, i) => (
                <li key={c.slug} className="flex items-baseline gap-3 border-t border-[#1C2430]/10 py-3">
                  <span className="li-mono w-5 shrink-0 text-[12px] text-[#5B6472]">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <Link to="/club/$slug" params={{ slug: c.slug }} className="font-bold text-[#1C2430] hover:text-[#2C4FD6]">
                      {c.name}
                    </Link>
                    <span className="text-sm text-[#5B6472]"> · {c.town}, {c.country}</span>
                  </div>
                  <span className="shrink-0 font-bold tabular-nums text-[#1C2430]">€{c.price}</span>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <section className="mt-14 rounded-2xl border border-[#1C2430]/15 bg-white/50 p-6">
          <h2 className="li-display text-lg font-bold tracking-tight text-[#1C2430]">
            Using this data
          </h2>
          <p className="mt-2 max-w-[70ch] text-sm leading-relaxed text-[#5B6472]">
            Journalists and researchers are welcome to cite this index with a link to this
            page. Every underlying price is visible on the club&apos;s own page with a
            last-checked date, and the{" "}
            <Link to="/how-we-verify" className="text-[#2C4FD6] underline underline-offset-4">
              methodology
            </Link>{" "}
            explains what we count and what we refuse to count. Spotted a rate that has
            changed? Mail{" "}
            <a href="mailto:hello@sunbedly.com" className="text-[#2C4FD6] underline underline-offset-4">
              hello@sunbedly.com
            </a>{" "}
            and we will re-check it against the club&apos;s published rates.
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}
